import { Dom } from "../../common/dom";

export class ListDropdown
{
    private readonly input: HTMLInputElement;
    private readonly menu: HTMLUListElement;
    private readonly onSelect: (value: string) => void;
    private readonly onOpen?: () => void;

    private entries: { value: string; el: HTMLLIElement }[] = [];
    private highlightIndex = -1;

    /**
     * @param input
     * @param host the element the menu is appended to. The menu is positioned
     *   absolutely, so the caller must ensure the host is positioned
     *   (e.g. `position: relative`); ListDropdown does not mutate the host's style.
     * @param onSelect called when an item is chosen (mouse or keyboard).
     * @param onOpen called whenever the user acts to open/refresh the list: focus,
     *   click, typing, or ArrowDown while closed. The owner decides what that means
     *   — show a static list, run a search, etc.
     */
    constructor(input: HTMLInputElement, host: HTMLElement,
                onSelect: (value: string) => void, onOpen?: () => void)
    {
        this.input = input;
        this.onSelect = onSelect;
        this.onOpen = onOpen;

        this.menu = Dom.add("ul", host, "dropdown-menu");
        Object.assign(this.menu.style, {
            position: "absolute",
            top: "100%",
            left: "0",
            display: "none",
            width: "100%",
            maxHeight: "240px",
            overflowY: "auto",
            marginTop: "2px",
            zIndex: "1000"
        });

        this.input.addEventListener("focus", () => this.onOpen?.());
        this.input.addEventListener("click", () => this.onOpen?.());
        this.input.addEventListener("input", () => this.onOpen?.());
        this.input.addEventListener("keydown", (e) => this.onKeyDown(e));
        // delay so a mousedown selection on the menu fires before the menu hides
        this.input.addEventListener("blur", () => setTimeout(() => this.hideMenu(), 150));
    }

    get items(): ReadonlyArray<string>
    {
        return this.entries.map((entry) => entry.value);
    }

    setItems(values: string[])
    {
        this.menu.innerHTML = "";
        this.entries = [];
        this.highlight(-1);

        for (const value of values)
        {
            const li = Dom.add("li", this.menu);
            const link = Dom.add("a", li);
            link.setAttribute("href", "#");
            link.textContent = value;
            // mousedown (not click) so it fires before the input loses focus
            link.addEventListener("mousedown", (e) =>
            {
                e.preventDefault();
                this.select(value);
            });
            this.entries.push({ value, el: li });
        }

        if (this.entries.length === 0)
        {
            this.hideMenu();
        }
    }

    showMenu()
    {
        this.menu.style.display = "block";
        this.menu.scrollTop = 0;
    }

    private hideMenu()
    {
        this.menu.style.display = "none";
        this.highlight(-1);
    }

    highlight(index: number, scrollToTop = false)
    {
        if (this.highlightIndex >= 0 && this.entries[this.highlightIndex])
        {
            this.entries[this.highlightIndex].el.classList.remove("active");
        }
        this.highlightIndex = index;
        if (index >= 0 && this.entries[index])
        {
            const node = this.entries[index].el;
            node.classList.add("active");
            if (scrollToTop)
            {
                // bring the highlighted option to the top of the list
                this.menu.scrollTop = node.offsetTop;
            }
            else
            {
                node.scrollIntoView({ block: "nearest" });
            }
        }
    }

    private moveHighlight(direction: number)
    {
        if (this.entries.length === 0)
        {
            return;
        }
        let next = this.highlightIndex + direction;
        if (next < 0)
        {
            next = this.entries.length - 1;
        }
        else if (next >= this.entries.length)
        {
            next = 0;
        }
        this.highlight(next);
    }

    private select(value: string)
    {
        this.onSelect(value);
        this.hideMenu();
    }

    private onKeyDown(e: KeyboardEvent)
    {
        const isOpen = this.menu.style.display !== "none";
        switch (e.key)
        {
            case "ArrowDown":
                if (!isOpen)
                {
                    this.onOpen?.();
                }
                else
                {
                    this.moveHighlight(1);
                }
                e.preventDefault();
                break;
            case "ArrowUp":
                if (isOpen)
                {
                    this.moveHighlight(-1);
                    e.preventDefault();
                }
                break;
            case "Enter":
                if (isOpen && this.highlightIndex >= 0 && this.entries[this.highlightIndex])
                {
                    this.select(this.entries[this.highlightIndex].value);
                    e.preventDefault();
                }
                break;
            case "Escape":
                if (isOpen)
                {
                    this.hideMenu();
                }
                break;
            default:
                break;
        }
    }
}
